<script setup lang="ts">
import { useI18n } from "vue-i18n";
import CnmcDialog from "@/dashboard/production_strategy/issuance-reporting/es/CnmcDialog.vue";
import CnmcOutboundOrderSummary from "@/dashboard/production_strategy/issuance-reporting/es/CnmcOutboundOrderSummary.vue";
import { CNMC_SEDE_URL } from "@/dashboard/production_strategy/issuance-reporting/es/cnmcPresentation";
import type { OutboundFiling } from "@/dashboard/production_strategy/issuance-reporting/es/cnmcTypes";

/**
 * Opens right after a transfer or export is prepared: download the ZIP, submit it in the CNMC Sede
 * with the holder's certificate, then enter its Nº de Registro. The same card stays reachable from
 * the order in the Transactions tab.
 */
defineProps<{
  companyId: number;
  filing: OutboundFiling;
}>();
defineEmits<{ update: [filing: OutboundFiling] }>();
const visible = defineModel<boolean>({ required: true });

const { t } = useI18n();
const STEPS = ["download", "sede", "receipt"] as const;
</script>

<template>
  <CnmcDialog
    v-model="visible"
    :title="t(`cnmc.outbound.dialog.title_${filing.route}`)"
    :eyebrow="t('cnmc.outbound.dialog.eyebrow')"
    wide
  >
    <div class="p-3 p-md-4 d-flex flex-column gap-4">
      <ol class="cnmc-steps list-unstyled d-grid gap-2 mb-0">
        <li
          v-for="(step, index) in STEPS"
          :key="step"
          class="d-flex gap-2 align-items-start"
        >
          <span
            class="badge rounded-pill bg-primary-subtle text-primary-emphasis"
          >
            {{ index + 1 }}
          </span>
          <div class="small">
            <div class="fw-semibold">
              {{ t(`cnmc.outbound.dialog.steps.${step}.title`) }}
            </div>
            <div class="text-body-secondary">
              {{ t(`cnmc.outbound.dialog.steps.${step}.body`) }}
            </div>
            <a
              v-if="step === 'sede'"
              :href="CNMC_SEDE_URL"
              target="_blank"
              rel="noopener noreferrer"
              >{{ t("cnmc.outbound.sede_link") }}</a
            >
          </div>
        </li>
      </ol>
      <CnmcOutboundOrderSummary
        :companyId
        :filing
        @update="$emit('update', $event)"
      />
    </div>
  </CnmcDialog>
</template>

<style scoped lang="scss">
.cnmc-steps {
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
}
</style>
